import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, cn, Divider, Link } from "@nextui-org/react";
import { faMemo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Breakpoint, CenButton, useBreakpoint } from "cenero-ui-library";
import { useBookingsCalendarStore } from "@/store/BookingsCalendarStore";
import { getFormattedTime, isBookingInProgress, isPastBookingComplete } from "@/lib/helpers";
import { IBookingCalendar } from "@/lib/types";
import BookingCalendarItem from "./BookingCalendarItem";

// ---------------------- Props

interface BookingCalendarProps {
  date: string;
  booking: IBookingCalendar;
  last: boolean;
}

// ---------------------- Component

const BookingCalendar = ({ date, booking, last }: BookingCalendarProps) => {
  // --------- Variables / Hooks

  const breakpoint = useBreakpoint();

  const isDesktop = breakpoint >= Breakpoint.xl;

  const navigate = useNavigate();

  const { registerBooking, setExpandedBooking } = useBookingsCalendarStore();

  const myId = booking.bookingId as string;

  const isComplete = isPastBookingComplete(booking);

  // --------- Effects

  useEffect(() => {
    registerBooking(date, myId);
  }, []);

  // --------- JSX

  return (
    <>
      <div className="flex h-auto w-full flex-row items-center justify-start border-gray-300 bg-white/20 transition-all duration-500 ease-in-out dark:border-gray-800 dark:bg-black/20">
        <div className="flex w-full flex-col items-center gap-3">
          <div
            className="flex h-auto w-full flex-row items-start justify-between pt-1"
            onClick={() => setExpandedBooking(date, myId)}
          >
            {/*******************************************/}
            {/* Booking Information */}
            {/*******************************************/}

            <div className="flex h-auto flex-grow flex-col items-start justify-start px-4">
              <BookingCalendarItem
                title="ORG"
                value={booking.clientRoom as string}
              />

              <BookingCalendarItem
                title="LOC"
                value={`${booking.city}, ${booking.state}`}
              />

              <BookingCalendarItem
                title="SCN"
                value={booking.scnNumber as string}
              />

              {isComplete ? (
                <>
                  <BookingCalendarItem
                    title="IN"
                    value={getFormattedTime(booking.localStartTime)}
                  />

                  <BookingCalendarItem
                    title="OUT"
                    value={
                      isBookingInProgress(booking)
                        ? "In Progress"
                        : getFormattedTime(booking.localEndTime)
                    }
                  />
                </>
              ) : (
                <div className="mt-2 text-xs text-[#ff321c] sm:text-sm">
                  <p className="font-light">Booking Not Completed</p>
                  <p className="font-light">Status: {booking.bookingStatusName}</p>
                  <p className="font-light">
                    Expected Start: {getFormattedTime(booking.localStartTime)}
                  </p>
                </div>
              )}

              {isBookingInProgress(booking) && (
                <CenButton
                  className="mt-3 h-8 w-24 text-xs xs:h-10 xs:text-sm sm:h-12 sm:w-32 sm:text-base"
                  color="primary"
                  label="Check Out"
                  variation="solid"
                  href={`checkout/${myId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isDesktop) {
                      navigate(`checkout/${myId}`, { replace: true });
                    } else {
                      navigate(`checkout/${myId}`);
                    }
                  }}
                />
              )}
            </div>

            {/*******************************************/}
            {/* Show Details Button */}
            {/*******************************************/}

            <div className="-mt-1 mr-1 flex h-auto flex-row items-center justify-end">
              <Button
                isIconOnly
                radius="full"
                className="bg-transparent p-2 text-current transition-all duration-200"
                as={Link}
                href={`details/${myId}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (isDesktop) {
                    navigate(`details/${myId}`, { replace: true });
                  } else {
                    navigate(`details/${myId}`);
                  }
                }}
              >
                <FontAwesomeIcon
                  icon={faMemo}
                  className="h-6 text-gray-400 transition-transform duration-500"
                />
              </Button>
            </div>
          </div>

          <Divider className={cn("w-11/12", [last && "hidden"])} />
        </div>
      </div>
    </>
  );
};

export default BookingCalendar;
